import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import QRCode from 'qrcode';
import api from '../../utils/api';
import Topbar from '../../components/shared/Topbar';
import EmployeeSidebar from '../../components/employee/EmployeeSidebar';
import LegalFooter from '../../components/shared/LegalFooter';
import '../../styles/global.css';

;
;
;

const CATEGORIES = [
  { value: 'isp_contract', label: 'ISP Contract' },
  { value: 'lease_addendum', label: 'Lease Addendum' },
  { value: 'marketing', label: 'Marketing Material' },
  { value: 'welcome_letter', label: 'Welcome Letter' },
  { value: 'general', label: 'General' },
];

const CAT_COLORS = {
  isp_contract: 'badge-blue',
  lease_addendum: 'badge-orange',
  marketing: 'badge-green',
  welcome_letter: 'badge-green',
  general: 'badge-gray',
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function buildFlyerHtml(price, savings) {
  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>Welcome to Affordable Telecom</title>
<style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Segoe UI',system-ui,sans-serif;background:#f4f7ff;color:#1a1f3a}</style>
</head><body>
<div style="max-width:640px;margin:40px auto;background:#fff;border-radius:16px;overflow:hidden;border:1px solid #dde4f5">
  <div style="background:#1E2761;padding:40px 32px;text-align:center">
    <div style="font-size:14px;color:#4FC3F7;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;margin-bottom:12px">Affordable Telecom LLC</div>
    <h1 style="color:#fff;font-size:28px;font-weight:800;line-height:1.2;margin-bottom:12px">Premium Internet for Your Building</h1>
    <p style="color:rgba(255,255,255,0.6);font-size:15px">Faster speeds. Lower prices. No contracts.</p>
  </div>
  <div style="padding:36px 32px">
    <div style="text-align:center;margin-bottom:28px">
      <div style="font-size:48px;font-weight:900;color:#1E2761">$${price}<span style="font-size:18px;font-weight:400;color:#5a6285">/mo</span></div>
      <div style="font-size:15px;color:#00C49F;font-weight:700;margin-top:4px">Save $${savings}/mo vs. retail pricing</div>
    </div>
    <div style="background:#f4f7ff;border-radius:12px;padding:20px 24px;margin-bottom:24px">
      <div style="font-weight:700;color:#1E2761;margin-bottom:10px;font-size:15px">What you get:</div>
      <div style="display:flex;flex-direction:column;gap:8px">
        <div style="display:flex;align-items:center;gap:8px;font-size:14px"><span style="color:#00C49F;font-weight:700">&#10003;</span> High-speed internet</div>
        <div style="display:flex;align-items:center;gap:8px;font-size:14px"><span style="color:#00C49F;font-weight:700">&#10003;</span> Professional installation included</div>
        <div style="display:flex;align-items:center;gap:8px;font-size:14px"><span style="color:#00C49F;font-weight:700">&#10003;</span> 24/7 customer support</div>
        <div style="display:flex;align-items:center;gap:8px;font-size:14px"><span style="color:#00C49F;font-weight:700">&#10003;</span> No contracts or hidden fees</div>
        <div style="display:flex;align-items:center;gap:8px;font-size:14px"><span style="color:#00C49F;font-weight:700">&#10003;</span> Easy online account management</div>
      </div>
    </div>
    <div style="text-align:center;margin-bottom:20px">
      <div style="display:inline-block;background:#1E2761;color:#fff;padding:14px 36px;border-radius:10px;font-weight:700;font-size:16px">Get Started at affordable-telecom.com/get-started</div>
    </div>
    <p style="text-align:center;font-size:13px;color:#5a6285">Scan the QR code in your lobby or visit the link above to set up your account in minutes.</p>
  </div>
  <div style="background:#f4f7ff;padding:16px 32px;text-align:center;font-size:12px;color:#5a6285">
    Affordable Telecom LLC &middot; New York, NY &middot; affordable-telecom.com
  </div>
</div>
</body></html>`;
}

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [properties, setProperties] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const fileRef = useRef(null);

  // Marketing materials state
  const [flyerPrice, setFlyerPrice] = useState('39.99');
  const [flyerSavings, setFlyerSavings] = useState('20');
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [editPrice, setEditPrice] = useState('39.99');
  const [editSavings, setEditSavings] = useState('20');
  const qrCanvasRef = useRef(null);

  const [uploadForm, setUploadForm] = useState({
    property_id: '',
    landlord_id: '',
    category: 'general',
    description: '',
    visible_to_tenant: false,
    visible_to_landlord: true,
  });

  const loadDocs = () => {
    api.get('/documents')
      .then(res => setDocuments(res.data.documents))
      .catch(() => toast.error('Could not load documents'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    Promise.all([
      api.get('/admin/properties'),
      api.get('/employee/landlords'),
    ]).then(([pRes, lRes]) => {
      setProperties(pRes.data.properties || []);
      setLandlords(lRes.data.landlords || []);
    }).catch(() => {});
    loadDocs();
  }, []);

  const handleUpload = async () => {
    const file = fileRef.current?.files[0];
    if (!file) { toast.error('Please select a file'); return; }
    if (!uploadForm.property_id && !uploadForm.landlord_id) {
      toast.error('Please select a property or landlord'); return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      Object.entries(uploadForm).forEach(([k, v]) => formData.append(k, v));

      await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded successfully!');
      setShowUpload(false);
      setUploadForm({ property_id: '', landlord_id: '', category: 'general', description: '', visible_to_tenant: false, visible_to_landlord: true });
      if (fileRef.current) fileRef.current.value = '';
      loadDocs();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed. Make sure Supabase Storage is configured.');
    } finally {
      setUploading(false);
    }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success('Document deleted');
      loadDocs();
    } catch {
      toast.error('Could not delete document');
    }
  };

  const filtered = documents.filter(d => filterCategory === 'all' || d.category === filterCategory);

  const downloadFlyer = () => {
    const html = buildFlyerHtml(flyerPrice, flyerSavings);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'tenant_welcome_flyer.html'; a.click();
    URL.revokeObjectURL(url);
  };

  const previewFlyer = () => {
    const html = buildFlyerHtml(flyerPrice, flyerSavings);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  const savePriceEdit = () => {
    setFlyerPrice(editPrice);
    setFlyerSavings(editSavings);
    setShowPriceModal(false);
    toast.success('Flyer prices updated');
  };

  // Render QR code to canvas
  const renderQR = useCallback(() => {
    if (!qrCanvasRef.current) return;
    QRCode.toCanvas(qrCanvasRef.current, 'https://portal.affordable-telecom.com/get-started', {
      width: 240,
      margin: 2,
      color: { dark: '#1e3a5f', light: '#ffffff' },
    });
  }, []);

  useEffect(() => { renderQR(); }, [renderQR]);

  const downloadQRPng = () => {
    if (!qrCanvasRef.current) return;
    const url = qrCanvasRef.current.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url; a.download = 'affordable_telecom_qr_code.png'; a.click();
  };

  const downloadDocx = async (type) => {
    const map = {
      'Partnership Letter': ['/docx-assets/partnership_b64.txt', 'bronstein_partnership_letter.docx'],
      'Bronstein Intro Letter': ['/docx-assets/intro_b64.txt', 'bronstein_intro_letter.docx'],
      'Service Agreement': ['/docx-assets/agreement_b64.txt', 'affordable_telecom_service_agreement.docx'],
    };
    const [assetPath, filename] = map[type];

    const response = await fetch(assetPath);
    const b64 = (await response.text()).trim();
    const byteChars = atob(b64);
    const byteArr = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
    const blob = new Blob([byteArr], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="app-layout">
      <Topbar role="employee" />
      <div className="dashboard-layout">
        <EmployeeSidebar />
        <main id="main-content" className="main-content">
          <div className="flex-between" style={{ marginBottom: 20 }}>
            <div>
              <div className="page-title">Documents</div>
              <div className="page-sub" style={{ margin: 0 }}>Upload and manage contracts, marketing materials, and property documents.</div>
            </div>
            <button className="btn btn-primary" onClick={() => setShowUpload(!showUpload)}>
              {showUpload ? 'Cancel' : '+ Upload document'}
            </button>
          </div>

          {/* Upload form */}
          {showUpload && (
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-header"><div className="card-title">Upload new document</div></div>
              <div className="card-body">
                <div style={{ background: '#f0f9ff', border: '1px solid var(--accent)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--teal)' }}>
                  💡 Documents are stored in Supabase Storage. Make sure you have created a public bucket named "documents" in your Supabase project.
                </div>

                {/* File picker */}
                <div className="form-group">
                  <label className="form-label">File (PDF, Word, PNG, JPEG — max 20MB)</label>
                  <input type="file" ref={fileRef} className="form-input"
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" />
                </div>

                <div className="two-col">
                  <div className="form-group">
                    <label className="form-label">Property (optional)</label>
                    <select className="form-input" value={uploadForm.property_id}
                      onChange={e => setUploadForm({ ...uploadForm, property_id: e.target.value })}>
                      <option value="">Select property</option>
                      {properties.map(p => <option key={p.id} value={p.id}>{p.name} — {p.city}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Or landlord (optional)</label>
                    <select className="form-input" value={uploadForm.landlord_id}
                      onChange={e => setUploadForm({ ...uploadForm, landlord_id: e.target.value })}>
                      <option value="">Select landlord</option>
                      {landlords.map(l => <option key={l.id} value={l.id}>{l.first_name} {l.last_name} — {l.company_name}</option>)}
                    </select>
                  </div>
                </div>

                <div className="two-col">
                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <select className="form-input" value={uploadForm.category}
                      onChange={e => setUploadForm({ ...uploadForm, category: e.target.value })}>
                      {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description (optional)</label>
                    <input className="form-input" placeholder="Brief description of this document"
                      value={uploadForm.description}
                      onChange={e => setUploadForm({ ...uploadForm, description: e.target.value })} />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={uploadForm.visible_to_landlord}
                      onChange={e => setUploadForm({ ...uploadForm, visible_to_landlord: e.target.checked })} />
                    Visible to landlord
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={uploadForm.visible_to_tenant}
                      onChange={e => setUploadForm({ ...uploadForm, visible_to_tenant: e.target.checked })} />
                    Visible to tenant
                  </label>
                </div>

                <button className="btn btn-primary" onClick={handleUpload} disabled={uploading}>
                  {uploading ? 'Uploading...' : 'Upload Document'}
                </button>
              </div>
            </div>
          )}

          {/* Filter */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <button className={`btn btn-sm ${filterCategory === 'all' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setFilterCategory('all')}>All</button>
            {CATEGORIES.map(c => (
              <button key={c.value} className={`btn btn-sm ${filterCategory === c.value ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setFilterCategory(c.value)}>{c.label}</button>
            ))}
          </div>

          {/* Documents list */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">All documents ({filtered.length})</div>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>File</th><th>Property</th><th>Category</th><th>Size</th><th>Visibility</th><th>Uploaded by</th><th>Date</th><th></th></tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-sub)' }}>No documents yet</td></tr>
                  )}
                  {filtered.map(d => (
                    <tr key={d.id}>
                      <td>
                        <a href={d.file_url} target="_blank" rel="noreferrer"
                          style={{ color: 'var(--navy)', fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>
                          📄 {d.file_name}
                        </a>
                        {d.description && <div style={{ fontSize: 11, color: 'var(--text-sub)' }}>{d.description}</div>}
                      </td>
                      <td style={{ fontSize: 12 }}>{d.property_name || '—'}</td>
                      <td>
                        <span className={`badge ${CAT_COLORS[d.category] || 'badge-gray'}`}>
                          {CATEGORIES.find(c => c.value === d.category)?.label || d.category}
                        </span>
                      </td>
                      <td style={{ fontSize: 12 }}>{fmtSize(d.file_size)}</td>
                      <td style={{ fontSize: 11 }}>
                        {d.visible_to_landlord && <span className="badge badge-blue" style={{ marginRight: 4 }}>Landlord</span>}
                        {d.visible_to_tenant && <span className="badge badge-green">Tenant</span>}
                      </td>
                      <td style={{ fontSize: 12 }}>{d.uploaded_by_name || '—'}</td>
                      <td style={{ fontSize: 12 }}>{fmtDate(d.created_at)}</td>
                      <td>
                        <button className="btn btn-outline btn-sm" style={{ color: 'var(--red)' }}
                          onClick={() => deleteDoc(d.id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {/* ── Marketing Materials ── */}
          <div style={{ marginTop: 28, marginBottom: 20 }}>
            <div className="page-title" style={{ fontSize: 18 }}>Marketing Materials</div>
            <div className="page-sub" style={{ margin: 0 }}>Downloadable flyers, QR codes, and partnership documents.</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            {/* 1. Tenant Welcome Flyer */}
            <div className="card">
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{
                  background: '#1E2761', borderRadius: 10, padding: '20px 16px', marginBottom: 14,
                  textAlign: 'center', color: 'white', position: 'relative',
                }}>
                  <div style={{ fontSize: 9, color: '#4FC3F7', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>Affordable Telecom LLC</div>
                  <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 6 }}>Premium Internet</div>
                  <div style={{ fontSize: 26, fontWeight: 900 }}>${flyerPrice}<span style={{ fontSize: 12, fontWeight: 400, color: 'rgba(255,255,255,0.5)' }}>/mo</span></div>
                  <div style={{ fontSize: 10, color: '#00C49F', fontWeight: 700, marginTop: 2 }}>Save ${flyerSavings}/mo</div>
                </div>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)', marginBottom: 4 }}>Tenant Welcome Flyer</div>
                <div style={{ fontSize: 11, color: 'var(--text-sub)', marginBottom: 12, flex: 1 }}>Branded HTML flyer with pricing and features. Print or email to tenants.</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={downloadFlyer}>Download</button>
                  <button className="btn btn-outline btn-sm" style={{ flex: 1 }} onClick={previewFlyer}>Preview</button>
                </div>
                <button className="btn btn-outline btn-sm" style={{ marginTop: 6, width: '100%' }} onClick={() => {
                  setEditPrice(flyerPrice); setEditSavings(flyerSavings); setShowPriceModal(true);
                }}>Edit Price</button>
              </div>
            </div>

            {/* 2. QR Code */}
            <div className="card">
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{ textAlign: 'center', marginBottom: 14, padding: '12px 0' }}>
                  <canvas ref={qrCanvasRef} style={{ maxWidth: '100%', height: 'auto' }} />
                </div>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)', marginBottom: 4 }}>QR Code</div>
                <div style={{ fontSize: 11, color: 'var(--text-sub)', marginBottom: 12, flex: 1 }}>
                  Points to portal.affordable-telecom.com/get-started. Post in lobbies and welcome packets.
                </div>
                <button className="btn btn-primary btn-sm" style={{ width: '100%' }} onClick={downloadQRPng}>Download PNG</button>
              </div>
            </div>

            {/* 3. Partnership Letter */}
            <div className="card">
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{ background: 'var(--light-bg)', borderRadius: 10, padding: '28px 16px', textAlign: 'center', marginBottom: 14 }}>
                  <div style={{ fontSize: 36 }} aria-hidden="true">📋</div>
                </div>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)', marginBottom: 4 }}>Partnership Letter</div>
                <div style={{ fontSize: 11, color: 'var(--text-sub)', marginBottom: 12, flex: 1 }}>Bronstein Properties partnership letter template (DOCX).</div>
                <button className="btn btn-primary btn-sm" style={{ width: '100%' }} onClick={() => downloadDocx('Partnership Letter')}>Download DOCX</button>
              </div>
            </div>

            {/* 4. Intro Letter */}
            <div className="card">
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{ background: 'var(--light-bg)', borderRadius: 10, padding: '28px 16px', textAlign: 'center', marginBottom: 14 }}>
                  <div style={{ fontSize: 36 }} aria-hidden="true">📨</div>
                </div>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)', marginBottom: 4 }}>Bronstein Intro Letter</div>
                <div style={{ fontSize: 11, color: 'var(--text-sub)', marginBottom: 12, flex: 1 }}>Introduction letter for Bronstein Properties tenants (DOCX).</div>
                <button className="btn btn-primary btn-sm" style={{ width: '100%' }} onClick={() => downloadDocx('Bronstein Intro Letter')}>Download DOCX</button>
              </div>
            </div>

            {/* 5. Service Agreement */}
            <div className="card">
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{ background: 'var(--light-bg)', borderRadius: 10, padding: '28px 16px', textAlign: 'center', marginBottom: 14 }}>
                  <div style={{ fontSize: 36 }} aria-hidden="true">📝</div>
                </div>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)', marginBottom: 4 }}>Service Agreement</div>
                <div style={{ fontSize: 11, color: 'var(--text-sub)', marginBottom: 12, flex: 1 }}>Affordable Telecom service agreement template (DOCX).</div>
                <button className="btn btn-primary btn-sm" style={{ width: '100%' }} onClick={() => downloadDocx('Service Agreement')}>Download DOCX</button>
              </div>
            </div>
          </div>

          {/* Price Edit Modal */}
          {showPriceModal && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100,
              background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }} onClick={e => { if (e.target === e.currentTarget) setShowPriceModal(false); }}>
              <div style={{ background: 'white', borderRadius: 16, padding: 28, width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>Edit Flyer Pricing</h3>
                <p style={{ fontSize: 13, color: 'var(--text-sub)', marginBottom: 20 }}>Update the price and savings shown on the flyer.</p>
                <div className="form-group">
                  <label className="form-label" htmlFor="edit-price">Monthly price ($)</label>
                  <input id="edit-price" className="form-input" type="number" step="0.01" min="0"
                    value={editPrice} onChange={e => setEditPrice(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="edit-savings">Monthly savings ($)</label>
                  <input id="edit-savings" className="form-input" type="number" step="0.01" min="0"
                    value={editSavings} onChange={e => setEditSavings(e.target.value)} />
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                  <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowPriceModal(false)}>Cancel</button>
                  <button className="btn btn-primary" style={{ flex: 1 }} onClick={savePriceEdit}>Save</button>
                </div>
              </div>
            </div>
          )}

          <LegalFooter />
        </main>
      </div>
    </div>
  );
}
